import { useMemo, useRef, useContext, useEffect, useState, type RefObject } from "react";
import {
  Box,
  Typography,
  Avatar,
  Tooltip,
  CircularProgress,
  useTheme,
  Tabs,
  Tab,
  Divider,
  IconButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  extractDomainFromEmail,
  formatDurationFromSeconds,
  getAvatarColor,
  getAvatarInitials,
  getGroupStatusMap,
  isCondensedTranscript,
  isGroupAiCompleted,
  secondsToHHMMSS,
} from "../../utils/utils";
import CollateralService from "../../services/collaterals/CollateralService";
import { GlobalContext } from "../../context/GlobalContext";
import CustomVideoControls from "../MeetingDetails/CustomVideoPlayer";
import { useLocation } from "react-router-dom";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { useMediaPlaybackRecovery } from "../../hooks/useMediaPlaybackRecovery";
import "../../styles/common.css";
import dayjs from "dayjs";
import ParticipantBox from "./ParticipantBox";
import ArrowExpand from "../../assets/interaction-detail-page/arrow-expand.svg";
import ArrowCollapse from "../../assets/interaction-detail-page/arrow-collapse.svg";
import SearchIcon from "../../assets/common/search.svg";
import ColumnResizeIcon from "../../assets/interaction-detail-page/resize-icon.svg";

const getAssetName = (item: any) => item?.assetName?.toLowerCase() || "";
const isAudioAsset = (item: any) => {
  if (!item) return false;
  const name = getAssetName(item);
  return (
    item?.assetType === "audio" && (name.endsWith(".m4a") || name.endsWith(".mp3"))
  );
};
const isVideoAsset = (item: any) => getAssetName(item).endsWith(".mp4");
const isVideoOrAudioAsset = (item: any) => isVideoAsset(item) || isAudioAsset(item);
const isVideoClipAsset = (item: any) => item?.assetSource?.toLowerCase() === "7g_video_clip";
const isVideoClipVideoAsset = (item: any) =>
  isVideoClipAsset(item) && getAssetName(item).endsWith("_video_clip.mp4");
const isRecordingTabAsset = (item: any) => isVideoOrAudioAsset(item) || isVideoClipVideoAsset(item);
const isTranscriptAsset = (item: any) => {
  if (!item) return false;
  if (isVideoOrAudioAsset(item)) return false;
  const name = getAssetName(item);
  return (
    isCondensedTranscript(name) ||
    (item?.assetType === "transcript" && name.endsWith(".txt")) ||
    name.includes("transcript")
  );
};

const isCondensedTranscriptAsset = (item: any) => {
  if (!item) return false;
  if (isVideoOrAudioAsset(item)) return false;
  const name = getAssetName(item);
  return isCondensedTranscript(name);
};

const getTranscriptFile = (files: any[]) => {
  if (!files || files.length === 0) return null;
  const condensed = files.find(isCondensedTranscriptAsset);
  if (condensed) return condensed;
  return files.find(isTranscriptAsset) || null;
};

const getReferenceTime = (
  joiningCallTime: string | null,
  inCallRecording: string | null,
  lines: string[]
) => {
  const timestamps: dayjs.Dayjs[] = [];

  if (joiningCallTime) timestamps.push(dayjs.utc(joiningCallTime));
  if (inCallRecording) timestamps.push(dayjs.utc(inCallRecording));

  let transcriptStart: dayjs.Dayjs | null = null;
  for (const line of lines) {
    const match = line.match(/^(.+?),(.+?),(.+?) => (.*)$/);
    if (match) {
      transcriptStart = dayjs.utc(match[2].trim());
      break;
    }
  }

  if (!transcriptStart) return dayjs.utc();

  timestamps.push(transcriptStart);
  const sorted = timestamps.sort((a, b) => a.valueOf() - b.valueOf());

  let reference = sorted[0];
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].isSame(transcriptStart)) break;
    reference = sorted[i];
  }
  return reference;
};

const secondsFromReference = (time: dayjs.Dayjs, referenceTime: dayjs.Dayjs) =>
  time.diff(referenceTime, "millisecond") / 1000;


interface CallStatsPageProps {
  interactionId: string;
  meetingData: any[];
  collateralData: any;
  isTeamInteraction: boolean;
  interactionDetails?: any;
  isInteractionAttendee?: boolean;
  onClipSaved?: (clipName: string) => void;
  groupId: string | null;
  resumeTimes: Record<string, number>;
  setResumeTimes: React.Dispatch<React.SetStateAction<Record<string, number>>>;
}

const CallStatsPage = ({
  interactionId,
  meetingData,
  collateralData,
  isTeamInteraction,
  interactionDetails,
  onClipSaved,
  groupId,
  resumeTimes,
  setResumeTimes,
}: CallStatsPageProps) => {
  const location = useLocation();
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const resumeTimesRef = useRef(resumeTimes);
  resumeTimesRef.current = resumeTimes;
  const { setPipState, featureMap } = useContext(GlobalContext);
  const params = new URLSearchParams(location.search);
  const [currentTime, setCurrentTime] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [transcripts, setTranscripts] = useState<any[]>([]);
  const transcriptRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const ScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [ selectedRecordingIndex, setSelectedRecordingIndex] = useState(0);
  const [selectedMediaAssetId, setSelectedMediaAssetId] = useState<string | null>(null);
  const [videoAssetIds, setVideoAssetIds] = useState<string[]>([]);
  const [transcriptAssetIds, setTranscriptAssetIds] = useState<string[]>([]);
  const [savedfiles, setSavedFiles] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchIndexes, setMatchIndexes] = useState<number[]>([]);
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [searchNavigationTick, setSearchNavigationTick] = useState(0);
  const [isParticipantsExpanded, setIsParticipantsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const isTranscriptDownlaodEnabled = featureMap?.interaction_call_stats_transcript_download === true;
  const { impersonationDetails } = useContext(GlobalContext);
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const findMatchedAssets = (selectedFile: any, files: any[]) => {
    if (!selectedFile) return { matchedVideo: null, matchedTranscript: null };

    const selectedSource = selectedFile.assetSource?.toLowerCase();
    const selectedName = selectedFile.assetName?.toLowerCase() || "";
    const matchToken =
      selectedName.match(/(.*)_video_recording\.mp4$/)?.[1] ||
      selectedName.match(/(.*)_(?:condensed|condense)_transcript\.txt$/)?.[1] ||
      selectedName.match(/(.*)\.(?:m4a|mp3)$/)?.[1];

    const sameSourceFiles = files.filter(
      (f) => !selectedSource || f.assetSource?.toLowerCase() === selectedSource
    );
    const matchedVideo = sameSourceFiles.find((f) => {
      const fName = f.assetName?.toLowerCase() || "";
      return (
        isVideoOrAudioAsset(f) &&
        (fName.includes(matchToken) ||
          fName.replace("_video_recording.mp4", "").replace(/\.(m4a|mp3)$/, "") ===
          getAssetName(f))
      );
    }) || files.find(isVideoOrAudioAsset);

    const sameSourceTranscripts = sameSourceFiles.filter(isTranscriptAsset);
    const allTranscripts = files.filter(isTranscriptAsset);

    // 1. First check for condensed transcript only
    const matchedCondensedTranscript =
      sameSourceTranscripts.find((f) => {
        const fName = getAssetName(f);
        return isCondensedTranscriptAsset(f) && (matchToken ? fName.includes(matchToken) : true);
      }) ||
      allTranscripts.find((f) => {
        const fName = getAssetName(f);
        return isCondensedTranscriptAsset(f) && (matchToken ? fName.includes(matchToken) : true);
      }) ||
      sameSourceTranscripts.find(isCondensedTranscriptAsset) ||
      allTranscripts.find(isCondensedTranscriptAsset);

    // 2. Only if condensed transcript is not there, fallback to transcript
    const matchedRegularTranscript =
      sameSourceTranscripts.find((f) => {
        const fName = getAssetName(f);
        return (
          fName.includes(matchToken) ||
          getAssetName(f) === selectedName.replace("_video_recording.mp4", "").replace(/\.(m4a|mp3)$/, "")
        );
      }) ||
      allTranscripts.find((f) => {
        const fName = getAssetName(f);
        return (
          fName.includes(matchToken) ||
          getAssetName(f) === selectedName.replace("_video_recording.mp4", "").replace(/\.(m4a|mp3)$/, "")
        );
      }) ||
      sameSourceTranscripts[0] ||
      allTranscripts[0];

    const matchedTranscript = matchedCondensedTranscript || matchedRegularTranscript || null;

    return { matchedVideo, matchedTranscript };
  };

  let selectedVideoId = "";
  let selectedTranscriptId = "";

  const hasVideoOrAudio = useMemo(() => savedfiles.some(isRecordingTabAsset), [savedfiles]);
  const tabFiles = useMemo(() => {
    if (!hasVideoOrAudio) return savedfiles;
    return savedfiles.filter(isRecordingTabAsset);
  }, [savedfiles, hasVideoOrAudio]);

  const selectedTabFile = tabFiles[selectedRecordingIndex];
  const isSelectedClip = isVideoClipAsset(selectedTabFile);
  const selectedPlayableFile = selectedTabFile;

  const playableFiles = useMemo(() => savedfiles.filter(isVideoOrAudioAsset), [savedfiles]);
  const currentPlayableIdx = useMemo(() => {
    if (!selectedTabFile || !isVideoOrAudioAsset(selectedTabFile)) return -1;
    return playableFiles.findIndex((v) => v.interactionAssetId === selectedTabFile.interactionAssetId);
  }, [selectedTabFile, playableFiles]);

  const selectedFile = selectedPlayableFile || (tabFiles.length > 0 ? tabFiles[selectedRecordingIndex] : undefined);

  const collateralsList = Array.isArray(collateralData?.data?.collaterals) ? collateralData?.data?.collaterals : [];

  const { matchedVideo, matchedTranscript } = findMatchedAssets(
    selectedFile,
    savedfiles.length > 0 ? savedfiles : collateralsList
  );

  if (groupId) {
    const groupFiles = savedfiles.filter(
      (file) => file.interactionGroupId === groupId
    );

    const mediaFile = groupFiles.find(isVideoOrAudioAsset);
    const transcriptFile = getTranscriptFile(groupFiles);

    selectedVideoId =
      (currentPlayableIdx !== -1
        ? videoAssetIds[currentPlayableIdx]
        : undefined) || mediaFile?.interactionAssetId;

    selectedTranscriptId =
      (currentPlayableIdx !== -1
        ? transcriptAssetIds[currentPlayableIdx]
        : undefined) || transcriptFile?.interactionAssetId;
  } else {
    const transcriptFile =
      getTranscriptFile(savedfiles) ||
      getTranscriptFile(collateralsList);

    selectedVideoId =
      (currentPlayableIdx !== -1
        ? videoAssetIds[currentPlayableIdx]
        : undefined) ||
      matchedVideo?.interactionAssetId ||
      (selectedFile && isVideoOrAudioAsset(selectedFile)
        ? selectedFile.interactionAssetId
        : undefined);

    selectedTranscriptId =
      (currentPlayableIdx !== -1
        ? transcriptAssetIds[currentPlayableIdx]
        : undefined) ||
      matchedTranscript?.interactionAssetId ||
      transcriptFile?.interactionAssetId;
  }

  if (selectedMediaAssetId) {
    selectedVideoId = selectedMediaAssetId;
  } else if (selectedPlayableFile && isVideoOrAudioAsset(selectedPlayableFile)) {
    selectedVideoId = selectedPlayableFile.interactionAssetId;
  }

  if (isVideoClipAsset(selectedTabFile)) {
    const clipId = selectedTabFile?.assetMetaData?.clip_id;
    const clipAssets = clipId != null
      ? collateralsList.filter((asset: any) =>
          String(asset?.assetMetaData?.clip_id) === String(clipId)
        )
      : [];
    const clipVideo = clipAssets.find(isVideoAsset) ||
      (isVideoAsset(selectedTabFile) ? selectedTabFile : undefined);
    const clipTranscript = clipAssets.find((asset: any) =>
      getAssetName(asset).endsWith("_transcript_clip.txt")
    ) || getTranscriptFile(clipAssets);
    selectedVideoId = clipVideo?.interactionAssetId || selectedTabFile?.interactionAssetId || "";
    selectedTranscriptId = clipTranscript?.interactionAssetId || "";
  }

  const selectedVideoAsset = collateralsList.find(
    (asset: any) => asset.interactionAssetId === selectedVideoId
  ) || matchedVideo;
  const selectedRecordingGroupId =
    selectedVideoAsset?.interactionGroupId || groupId || selectedFile?.interactionGroupId || "";
  const selectedRecordingAssets = selectedRecordingGroupId
    ? collateralsList.filter((asset: any) => asset.interactionGroupId === selectedRecordingGroupId)
    : [];
  const getCollateralFileName = (asset: any) =>
    (asset?.assetName || asset?.assetLocation || "").split("/").pop()?.toLowerCase() || "";

  const originalCondensedTranscriptAssetId = selectedRecordingAssets.find((asset: any) =>
    asset.assetType === "transcript" && getCollateralFileName(asset).endsWith("_condensed_transcript.txt")
  )?.interactionAssetId || "";

  const originalRawTranscriptAssetId = selectedRecordingAssets.find((asset: any) =>
    asset.assetType === "transcript" &&
    getCollateralFileName(asset).endsWith("_transcript.txt") &&
    !getCollateralFileName(asset).endsWith("_condensed_transcript.txt")
  )?.interactionAssetId || "";

  const [isClipping, setIsClipping] = useState(false);
  const [clipEditorResetKey, setClipEditorResetKey] = useState(0);
  const [clipRangeRequest, setClipRangeRequest] = useState<{
    start: number;
    id: number;
  } | null>(null);
  const togglePlayPause = (fullscreenOnly = false) => {
    const video = isAudioAsset(selectedTabFile) ? audioRef.current : videoRef.current;
    if (!video) return;
    if (fullscreenOnly && !document.fullscreenElement) {
      return;
    }

    if (video.paused) {
      wasPlayingRef.current = true;
      video.play();
    } else {
      wasPlayingRef.current = false;
      video.pause();
    }
  };
  
  const selectedTabs = location.state?.selectedTab ?? params.get("st") ?? "";
  const sharedWithMe = location.state?.sharedWithMe ?? selectedTabs === "sharedWithMe"? true : false;
  const sharedByMe = location.state?.sharedByMe ?? selectedTabs === "sharedByMe"? true : false;
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;


  const groupStatusMap = useMemo(() => {
    const files = Array.isArray(collateralData?.data?.collaterals) ? collateralData?.data?.collaterals : [];
    return getGroupStatusMap(files);
  }, [collateralData]);

  const isAiAnalysisCompleted = (item: any) => {
    if (!item?.interactionGroupId) {
      return false;
    }

    return isGroupAiCompleted(
      groupStatusMap[item.interactionGroupId] ?? []
    );
  };

  const {
    data: downloadData,
    refetch: refetchVideoUrl,
  } = CollateralService.useGetAssetDownloadUrl(
    {
      assetId: selectedVideoId,
      isTeamInteraction: isTeamInteraction,
      sharedWithMe: sharedWithMe || undefined,
      interactionId: interactionId,    
      sharedByMe: sharedByMe || undefined,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    !!selectedVideoId
  );

  const [downloadCollateral, setDownloadCollateral] = useState<any>("");
  const [downloadApiCall, setDownloadApiCall] = useState<boolean>(false);
  const [pendingDownloadFile, setPendingDownloadFile] = useState<any | null>(null);
  const [downloadApiResponse, setDownloadApiResponse] = useState<any>(undefined);
  const { showSnackbar } = useDisplaySnackbar();

  const {
    data: downloadApiData,
    isSuccess: isDownloadApiSuccess,
    isError: isDownloadApiError,
    error: downloadApiError,
  } = CollateralService.useDownloadCollateral(
    {
      assetId: downloadCollateral?.id || "",
      isTeamInteraction: isTeamInteraction,
      sharedWithMe: sharedWithMe || undefined,
      sharedByMe: sharedByMe || undefined,
      interactionId: interactionId,
    },
    !!downloadCollateral?.id && downloadApiCall
  );

  useEffect(() => {
    if (isDownloadApiSuccess && downloadApiData) {
      const downloadData = downloadApiData?.data;
      if (downloadData instanceof Blob) {
        setDownloadApiResponse(downloadData);
      } else {
        const blob = new Blob([downloadData], { type: "application/octet-stream" });
        setDownloadApiResponse(blob);
      }
    }
    if (isDownloadApiError) {
      showSnackbar("error", downloadApiError?.message || "Failed to download file");
    }
  }, [isDownloadApiSuccess, downloadApiData, isDownloadApiError, downloadApiError]);

  useEffect(() => {
    if (!pendingDownloadFile || !(downloadApiResponse instanceof Blob)) return;

    const blob = downloadApiResponse;
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = pendingDownloadFile.name || "file";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);

    setPendingDownloadFile(null);
    setDownloadApiResponse(undefined);
    setDownloadCollateral("");
    setDownloadApiCall(false);
  }, [downloadApiResponse, pendingDownloadFile]);

  const spritesheetAsset = useMemo(() => {
    const files = Array.isArray(collateralData?.data?.collaterals) ? collateralData?.data?.collaterals : [];
    return (
      files.find(
        (item: any) => item.assetType === "spritesheet" && item.assetName
      ) || null
    );
  }, [collateralData]);

  const { data: spritesheetDownloadData, refetch: refetchSpritesheetUrl } = CollateralService.useGetAssetDownloadUrl(
    {
      assetId: spritesheetAsset?.interactionAssetId,
      isTeamInteraction: isTeamInteraction,
      sharedWithMe: sharedWithMe || undefined,
      interactionId: interactionId,
      sharedByMe: sharedByMe || undefined,
    },
    !!spritesheetAsset?.interactionAssetId
  );

  const videoUrl = downloadData?.data?.downloadUrl;
  const videoUrlRef = useRef<string | undefined>(videoUrl);

  useEffect(() => {
    videoUrlRef.current = videoUrl;
  }, [videoUrl]);
  const { data: transcriptData, isLoading: transcriptLoading, isSuccess: isTranscriptSuccess } =
    CollateralService.UseGetTranscriptView(
      {
        interactionId,
        assetId: selectedTranscriptId,
      },
      !!selectedTranscriptId
    );

  const transcriptLines = useMemo(() => {
    const raw = transcriptData?.data?.transcript;
    if (!raw || typeof raw !== "string") return [];
    return raw.replace(/\r/g, "").trim().split("\n");
  }, [transcriptData]);

  const referenceTime = useMemo(() => {
    const joiningCallTime = transcriptData?.data?.joiningCallTime || null;
    const inCallRecording = transcriptData?.data?.inCallRecording || null;
    return getReferenceTime(joiningCallTime, inCallRecording, transcriptLines);
  }, [transcriptData, transcriptLines]);

  const effectiveReferenceTime = useMemo(() => {
    if (!isSelectedClip) return referenceTime;
    const clipReferenceTime = [
      transcriptData?.data?.clipReferenceTime,
    ].find((value) => typeof value === "string" && value.length > 0);

    if (!clipReferenceTime) return referenceTime;
    const parsedClipReferenceTime = dayjs.utc(clipReferenceTime);
    return parsedClipReferenceTime.isValid() ? parsedClipReferenceTime : referenceTime;
  }, [isSelectedClip, selectedTabFile, collateralsList, referenceTime, transcriptData]);

  const activeCollateralAsset = useMemo(() => {
    const currentGroupId = selectedTabFile?.interactionGroupId;
    const files = Array.isArray(collateralData?.data?.collaterals) ? collateralData?.data?.collaterals : [];

   if (currentGroupId) {
    return files.find(
      (f: any) => f.interactionGroupId === currentGroupId && f.assetMetaData?.talk_stats
    ) || null;
  }
  return files.find((f: any) => f.assetMetaData?.talk_stats) || null;
  }, [selectedTabFile, collateralData]);

  const effectiveParticipants = useMemo(() => {
    const talkStats = activeCollateralAsset?.assetMetaData?.talk_stats;
    const collateralParticipants = collateralData?.data?.participants ?? [];

    if (talkStats && Object.keys(talkStats).length > 0) {
      return Object.entries(talkStats).map(([key, stat]: [string, any]) => {
        // Find matching profile in collateralParticipants first for picture_url
        const matchedCollateralProfile = (collateralParticipants || [])?.find((p: any) => {
          if (stat.emp_email && p.email?.toLowerCase() === stat.emp_email.toLowerCase()) return true;
          return false;
        });

        // Find matching profile in meetingData as fallback
        const matchedProfile = (meetingData || []).find((p: any) => {
          if (stat.emp_email && p.email?.toLowerCase() === stat.emp_email.toLowerCase()) return true;
          return false;
        });
        const pictureUrl = matchedCollateralProfile?.picture_url || matchedProfile?.pictureUrl || "";

        const rawFrom = stat.participation || matchedProfile?.participantFrom || "other";
        const participantFrom = typeof rawFrom === "string" && rawFrom ? rawFrom.trim().toLowerCase() : "other";

        return {
          speakerName: stat.speaker_name || key,
          name: stat.speaker_name || key,
          email: stat.emp_email || matchedProfile?.email || "",
          pictureUrl: pictureUrl,
          duration: stat.talk_time ?? 0,
          participantFrom: participantFrom,
          attended: stat.attended ?? true,
          joinTime: stat.join_time,
          leaveTime: stat.leave_time,
        };
      });
    }

    return (meetingData || []).map((p: any) => ({
      ...p,
      participantFrom: typeof p?.participantFrom === "string" && p.participantFrom ? p.participantFrom.trim().toLowerCase() : "other",
    }));
  }, [activeCollateralAsset, collateralData, meetingData]);
  const meetingDuration = useMemo(() => {
    const durationFromMetadata = activeCollateralAsset?.assetMetaData?.transcriptDuration;
    if (
      activeCollateralAsset?.assetMetaData?.talk_stats &&
      durationFromMetadata &&
      Number(durationFromMetadata) > 0
    ) {
      return Number(durationFromMetadata);
    }

    if (transcriptLines.length === 0) return 1;
    const last = transcriptLines[transcriptLines.length - 1]
      .split(" => ")[0]
      ?.split(",") ?? [];
    const end = dayjs.utc((last?.[2] || "").trim());
    if (!end.isValid()) return 1;
    const duration = end.diff(effectiveReferenceTime, "second");

    return duration > 0 ? duration : 1;
  }, [activeCollateralAsset, transcriptLines, effectiveReferenceTime]);

  const speakerSegmentsMap = useMemo(() => {
    type Segment = { start: number; end: number; startTime: string; endTime: string };
    type SpeakerEntry = {
      participant: any | null;
      segments: Segment[];
      totalDuration: number | string | undefined;
      participantFrom: string;
    };

    const map: Record<string, SpeakerEntry> = {};
    effectiveParticipants?.forEach((p: any) => {
    const key = p?.email || p?.speakerName || `${p.name}-${p.email}`;
      map[key] = {
        participant: p,
        segments: [],
        totalDuration: p.duration ?? null,
        participantFrom: (p.participantFrom || "other").toString().trim().toLowerCase(),
      };
    });

    transcriptLines?.forEach((line) => {
        const [meta] = line.split(" => ");
        if (!meta) return;

      const [rawSpeaker, startRaw, endRaw] = meta.split(",").map(s => s.trim());
      const transcriptSpeaker = rawSpeaker?.split("[")[0]?.trim();
      const matched = effectiveParticipants?.find((p: any) => {
        const participantName = (p.speakerName || p.name)?.trim()?.toLowerCase();
        const transcriptName = transcriptSpeaker?.trim()?.toLowerCase();

        if (participantName === transcriptName) return true;
      });

      const startDate = dayjs.utc(startRaw);
      const endDate = dayjs.utc(endRaw);
      if (!startDate.isValid() || !endDate.isValid()) return;
      const start = secondsFromReference(startDate, effectiveReferenceTime);
      const end = secondsFromReference(endDate, effectiveReferenceTime);
      if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return;
      const key = matched?.email || matched?.speakerName || `${matched?.name || transcriptSpeaker}-${matched?.email || ""}`;

      if (!map[key]) {
        map[key] = {
          participant: matched || null,
          segments: [],
          totalDuration: matched?.duration ?? null,
          participantFrom: matched ? (matched.participantFrom || "other").toString().trim().toLowerCase() : "other",
        };
      }

      map[key].segments.push({ start, end, startTime: startRaw, endTime: endRaw });
    });

    return map;
  }, [transcriptLines, effectiveParticipants, effectiveReferenceTime]);

  const domain = useMemo(() => {
    const accountParticipant = effectiveParticipants.find(
      (p: any) => p.participantFrom?.toString().trim().toLowerCase() === "account"
    );
    const email = accountParticipant?.email;
    if (!email) return "Account";
    const extracted = extractDomainFromEmail(email);
    return extracted ? extracted.split(".")[0] : "Account";
  }, [effectiveParticipants]);


const renderSpeaker = (
  speaker: string,
  participant: any,
  totalDuration: number | string,
) => {
  const displayName =
    participant?.speakerName === participant?.name
      ? participant?.speakerName
      : `${participant?.speakerName || ""}${
          participant?.name ? ` (${participant.name})` : ""
        }`;
  const duration = Number(totalDuration) || 0;
  const actual = Number(interactionDetails?.actualDuration) || 0;
  const percent = actual > 0 && duration > 0 ? (duration / actual) * 100 : null;
  const percentText = percent != null ? `${Number(percent.toFixed(1))}%` : null;

  return (
  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      {participant ? (
        <Tooltip title={participant?.email} arrow>
          <Avatar src={participant?.pictureUrl || undefined} sx={{ width: 28, height: 28 }}>
            {participant.name?.[0]?.toUpperCase() || "U"}
          </Avatar>
        </Tooltip>
      ) : (
        <Avatar sx={{ width: 28, height: 28, bgcolor: "#BDBDBD" }}>
          {speaker[0]?.toUpperCase() || "U"}
        </Avatar>
      )}

    <Tooltip title={participant?.email || ""} arrow>
        <Typography variant="body2" sx={{ fontSize: "11px", color: "#33333B" }}>
          {displayName} <span style={{fontWeight: "bold"}}>:</span>        
        </Typography>
    </Tooltip>

    {totalDuration && totalDuration !== "0" && (
      <>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ fontSize: 13 }}
      >
        {formatDurationFromSeconds(Number(totalDuration))}
      </Typography>
      <Typography  variant="body2"
        color="textSecondary"
        sx={{ fontSize: 13 }}>
        {percentText && (
          <span style={{ fontSize: "13px", }}>
            ({percentText})
          </span>
        )}
      </Typography>
      </>
    )}
  </Box>
  )
};

  const withoutDurationAll = useMemo(() => {
    return Object.entries(speakerSegmentsMap).filter(
      ([, { totalDuration }]) => Number(totalDuration || 0) === 0
    );
  }, [speakerSegmentsMap]);


  const requestClipRange = (start: number) => {
    if (!isClipping) return;
    setClipRangeRequest({ start, id: Date.now() });
  };

  const seekAndPlayFrom = (start: number) => {
    const media = isAudioAsset(selectedTabFile) ? audioRef.current : videoRef.current;
    wasPlayingRef.current = true;
    if (media) {
      media.currentTime = start;
      media.play();
    }
    setCurrentTime(start);
    requestClipRange(start);
  };

  const handleSegmentClick = (start: number) => {
    seekAndPlayFrom(start);
  };

  useEffect(() => {
      if (isTranscriptSuccess && transcriptData) {
        const transcriptsData = transcriptData?.data?.transcript;
        const errorMessage = transcriptData?.data?.errorMessage;
        if (errorMessage) {
          setTranscript("");
        }
        else if (transcriptsData) {
          setTranscript(transcriptsData);
        }
        else {
          setTranscript("");
        }
      }
    }, [transcriptData, isTranscriptSuccess]);
  useEffect(() => {
    if(transcript){
      setTranscripts(parseTranscript(transcript));
    }
  },[transcript]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;

      const target = e.target as HTMLElement;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      ) return;

      const video = videoRef.current;
      if (!video) return;

      const isFullscreen =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).msFullscreenElement;

      if (!isFullscreen) return;

      e.preventDefault();
      togglePlayPause();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
    
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
  
    const handleEnterPiP = () => {
      setPipState(prev => ({
        ...prev,
        videoElement: videoRef.current as HTMLVideoElement,
        meetingId: interactionId,
        triggered: true,
      }));
    };
    
    video.addEventListener("enterpictureinpicture", handleEnterPiP);
    return () => {
      video.removeEventListener("enterpictureinpicture", handleEnterPiP);
    };
  }, [videoRef.current]);
  
  const convertToSeconds = (time: string): number => {
    const [hh, mm, ss] = time.replace(",",".").split(":");
    return parseInt(hh) * 3600 + parseInt(mm) * 60 + Math.floor(parseFloat(ss));
  };

  const parseTranscript = (rawText: any) => {
    const lines = typeof rawText === "string" ? rawText.replace(/\r/g, "").trim().split("\n") : [];
    const isWebVttFormat = lines.some((line: string) => line.trim().toUpperCase().startsWith("WEBVTT") );
      if (isWebVttFormat) {
        return parseWebVttFormat(lines);
      } else {
        return parseTimestampedFormat(lines, effectiveReferenceTime);
      }
  };

  const parseWebVttFormat = (lines: any) => {
    const result: any[] = [];
    for (let i = 0; i < lines.length; i++) {
      const match = lines[i].match(/(\d{2}:\d{2}:\d{2}[.,]\d{3}) --> (\d{2}:\d{2}:\d{2}[.,]\d{3})/);
      if (match && i + 1 < lines.length) {
        const startTime = convertToSeconds(match[1]);
        const endTime = convertToSeconds(match[2]);
        const speakerTextMatch = lines[i + 1].match(/^([^:]+):\s*(.*)$/);
        if (speakerTextMatch) {
          const speaker = speakerTextMatch[1].trim();
          const text = speakerTextMatch[2].trim();
          i++;
          while (i < lines.length && lines[i].trim() === "") {
            i++;
          }
          result.push({
            speaker,
            start: startTime,
            end: endTime,
            text,
          });
        }
      }
    }
    return result;
  };
  const parseTimestampedFormat = (lines: string[], sharedReferenceTime: dayjs.Dayjs) => {
    return lines
      .map((line) => {
        const match = line.match(/^(.+?),(.+?),(.+?) => (.*)$/);
        if (!match) return null;
        const [, speaker, startTimeRaw, endTimeRaw, text] = match;
        const start = dayjs.utc(startTimeRaw.trim());
        const end = dayjs.utc(endTimeRaw.trim());
        if (!start.isValid() || !end.isValid()) return null;
        return {
          speaker: speaker.replace(/\s*\[.*?\]/g, ''),
          start: secondsFromReference(start, sharedReferenceTime),
          end: secondsFromReference(end, sharedReferenceTime),
          text: text,
        };
      })
      .filter(Boolean);
  };


  useEffect(() => {
    const interval = setInterval(() => {
      const media = isAudioAsset(selectedTabFile) ? audioRef.current : videoRef.current;
      if (!media) return;
      setCurrentTime(media.currentTime);
    }, 500);
    return () => clearInterval(interval);
  }, [selectedTabFile]);

  useEffect(() => {
    const container = containerRef.current;
    const handleScroll = () => {
      setIsUserScrolling(true);
      if (ScrollTimeoutRef.current) {
        clearTimeout(ScrollTimeoutRef.current);
      }
      ScrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      },5000);
    }
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
      if (ScrollTimeoutRef.current)
        clearTimeout(ScrollTimeoutRef.current);
    };
  },[containerRef.current]);

  useEffect(() => {
    const video = isAudioAsset(selectedTabFile) ? audioRef.current : videoRef.current;
    if (isUserScrolling || !video || video.paused || isClipping) return;
    const activeIndex = transcripts.findIndex(
      (t:any) => currentTime >= t.start && currentTime <= t.end
    );
    if (activeIndex !== -1 && transcriptRefs.current[activeIndex]) {
      const el = transcriptRefs.current[activeIndex];
      const container = containerRef.current;

      if (el && container) {
        const containerTop = container.getBoundingClientRect().top;
        const elTop = el.getBoundingClientRect().top;
        const offset = 90;

        container.scrollTo({
          top: container.scrollTop + (elTop - containerTop) - offset,
          behavior: "smooth",
        });
      }
    }
  }, [currentTime, isUserScrolling, isClipping, selectedTabFile]);

  const formatTime = (time: number) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    return `${hours}:${minutes}:${seconds}`;
  };

  const handleTranscriptClick = (item: { start: number; end?: number }) => {
    seekAndPlayFrom(item.start);
    const activeIndex = transcripts.findIndex(
      (t: any) => item.start >= t.start && item.start <= t.end
    );
    if (activeIndex !== -1 && transcriptRefs.current[activeIndex]) {
      transcriptRefs?.current[activeIndex]?.scrollIntoView({
        behavior: "auto",
        block: "nearest",
      });
    }
  };

  const renderTranscriptContainer = () => {
    return (
      <>
        <Box className="callStats-search-container">
          <Box className="callStats-search">
            <img src={SearchIcon} width={16} height={16}/>
            <input
              type="text"
              placeholder="Search for keywords..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="callStats-searchInput"
            />
            {searchQuery && (
              <div className="callStats-searchActions">
                <span className="callStats-searchCount">
                  {matchIndexes.length > 0
                    ? `${activeMatchIndex + 1}/${matchIndexes.length}`
                    : "0"}
                </span>
                <span className="callStats-divider" />
                <img src={ArrowCollapse} className="classStats-icon" onClick={handlePrev}/>
                <img src={ArrowExpand} className="classStats-icon" onClick={handleNext}/>
                <button onClick={handleClear}>✕</button>
              </div>
            )}
          </Box>
          {isTranscriptDownlaodEnabled && !isImpersonating &&(
            <Box>
              <Tooltip title= { !transcript ? "Transcript not available" : "Download Transcript"} arrow placement="top">
                <span>
                <IconButton
                  size="small"
                  onClick={handleTranscriptDownlaod}
                  disabled={!transcript}
                >
                  <DownloadIcon sx={{ fontSize: 26, color: "#6036D6" }} />
                </IconButton>
                </span>
              </Tooltip>
            </Box> 
          )}
        </Box>
        {!transcripts || transcripts.length === 0 ? (
          <Typography sx={{ fontFamily: "var(--font-family)", fontSize: "12px" }}>
            Transcript not available.
          </Typography>
        ) : (
          transcripts.map((item: any, index: number) => {
            const isActive = currentTime >= item.start && currentTime <= item.end;
            return (
              <Box className="callStats-transcript-lines"
                key={index}
                ref={(el) => {
                      transcriptRefs.current[index] = el as HTMLDivElement | null;
                    }}
                sx={{
                  backgroundColor: isActive ? theme.palette.brand.light : "transparent",
                  fontWeight: isActive ? "bold" : "normal",
                }}
                onClick={() => handleTranscriptClick(item)}
              >
                <Avatar
                  className="callStats-transcript-avatar"
                  variant="square"
                  sx={{
                    bgcolor: getAvatarColor(item.speaker),
                  }}
                >
                  {getAvatarInitials(item.speaker)}
                </Avatar>
                <span style={{ lineHeight: "1.4" }}>
                  <strong>{item.speaker}</strong>{" "}
                  <span style={{ color: "#7A7A8C" }}>
                    ({formatTime(item.start)}):
                  </span>{" "}
                  {highlightText(item.text)}
                </span>
              </Box>
            );
          })
        )}
      </>
    );
  };

  useEffect(() => {
    const result = Array.isArray(collateralData?.data?.collaterals) ? collateralData?.data?.collaterals : [];
    let filteredResult = [];
    if (groupId) {
      // ✅ NEW LOGIC
      filteredResult = result.filter((item: any) => {
        const isRelevant =
          isVideoClipAsset(item) ||
          (((item.assetType === "transcript" || item.assetType === "video" || item.assetType === "audio") &&
            isVideoOrAudioAsset(item)) ||
          isAudioAsset(item) ||
          isTranscriptAsset(item) || isAiAnalysisCompleted(item));

        return item.interactionGroupId === groupId && isRelevant;
      });
    } else {
      // ✅ OLD LOGIC (fallback)
      filteredResult = result.filter((item: any) => {
        return (
          isVideoClipAsset(item) ||
          ((item.assetType === "transcript"  || item.assetType === "video" )&&
            isVideoOrAudioAsset(item)) ||
          isAudioAsset(item) ||
          isTranscriptAsset(item) ||
          isAiAnalysisCompleted(item)
        );
      });
    }
    filteredResult.sort((a: any, b: any) => {
      const getPriority = (item: any) => {
        if (isVideoAsset(item)) return 1;
        if (isAudioAsset(item)) return 2;
        return 3;
      };

      return getPriority(a) - getPriority(b);
    });

    setSavedFiles(filteredResult);

    // use filteredResult (not result)
    const playableAssets = filteredResult
      .filter(isVideoOrAudioAsset)
      .map((item: any) => item.interactionAssetId);

    const transcripts = filteredResult
      .filter(isTranscriptAsset)
      .sort((a: any, b: any) => {
        const aCondensed = isCondensedTranscriptAsset(a) ? 0 : 1;
        const bCondensed = isCondensedTranscriptAsset(b) ? 0 : 1;
        return aCondensed - bCondensed;
      })
      .map((item: any) => item.interactionAssetId);

    setVideoAssetIds(playableAssets);
    setTranscriptAssetIds(transcripts);
  }, [collateralData, groupId]);

  const groupTranscripts: Record<string, any> = {};
  transcripts.forEach((item) => {
    if (!groupTranscripts[item.speaker]) groupTranscripts[item.speaker] = [];
    groupTranscripts[item.speaker].push(item);
  });

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
      setIsClipping(false);
      setClipEditorResetKey((currentKey) => currentKey + 1);
      setSelectedRecordingIndex(newValue);
      setSelectedMediaAssetId(null);
      const selectedFile = tabFiles[newValue];
      if (selectedFile) {
        setTranscripts(parseTranscript(transcript));
      }
    };
    useEffect(() => {
    if (tabFiles?.length === 1) {
      setSelectedRecordingIndex(0);
      setTranscripts(parseTranscript(transcript));
    }
  }, [tabFiles]);

  const [leftWidth, setLeftWidth] = useState(50); // %

  const isDragging = useRef(false);

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.current) return;

    const container = document.getElementById("resizable-container");
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - rect.left) / rect.width) * 100;

    if (newWidth > 36 && newWidth < 80) {
      setLeftWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMouseDown = () => {
    isDragging.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  const highlightText = (text: string) => {
    if (!searchQuery.trim()) return text;

    const normalizedSearch = searchQuery.trim().replace(/\s+/g, " ");

    const escaped = normalizedSearch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // escape regex
    const regex = new RegExp(`(${escaped})`, "gi");

    return text.split(regex).map((part, i) =>
      part.toLowerCase() === normalizedSearch.toLowerCase() ? (
        <span key={i} style={{ background: "#D6D6DB" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchQuery(inputValue);
    }, 300); // debounce delay

    return () => clearTimeout(timeout);
  }, [inputValue]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setMatchIndexes([]);
      setActiveMatchIndex(0);
      return;
    }

    const matches: number[] = [];

    transcripts.forEach((item, index) => {
      const normalizedSearch = searchQuery.trim().replace(/\s+/g, " ");
      if (item.text.toLowerCase().includes(normalizedSearch.toLowerCase())) {
        matches.push(index);
      }
    });

    setMatchIndexes(matches);
    setActiveMatchIndex(0);
    setSearchNavigationTick(0);
  }, [searchQuery, transcripts]);

  useEffect(() => {
    if (matchIndexes.length === 0) return;

    const index = matchIndexes[activeMatchIndex];
    const el = transcriptRefs.current[index];

    const container = containerRef.current;
    if (el && container) {
      const containerTop = container.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;

      const offset = 80;

      container.scrollTo({
        top: container.scrollTop + (elTop - containerTop) - offset,
        behavior: "smooth",
      });
    }
  }, [activeMatchIndex, matchIndexes, searchNavigationTick]);

  const handleNext = () => {
    if (matchIndexes.length === 0) return;
    if (matchIndexes.length === 1) {
      setSearchNavigationTick((prev) => prev + 1);
      return;
    }
    setActiveMatchIndex((prev) => (prev + 1) % matchIndexes.length);
  };

  const handlePrev = () => {
    if (matchIndexes.length === 0) return;
    if (matchIndexes.length === 1) {
      setSearchNavigationTick((prev) => prev + 1);
      return;
    }
    setActiveMatchIndex((prev) =>
      prev === 0 ? matchIndexes.length - 1 : prev - 1
    );
  };

  const handleClear = () => {
    setSearchQuery("");
    setMatchIndexes([]);
    setActiveMatchIndex(0);
    setSearchNavigationTick(0);
    setInputValue("");
  };

  const handleTranscriptDownlaod = () => {
    if (!transcript) return;
    const rawFiles = collateralData?.data?.collaterals ?? [];
    const files = Array.isArray(rawFiles) ? rawFiles : [];
    const transcriptName = files.find((items: any) => items.interactionAssetId === selectedTranscriptId)?.assetName || "transcript.txt";
    
    const processedTranscript = transcript
      .split("\n")
      .map((line) => {
        const timestampedMatch = line.match(/^(.+?),(.+?),(.+?) => (.*)$/);
        if (timestampedMatch) {
          const [, speaker, start, end, text] = timestampedMatch;
          const cleanedSpeaker = speaker.replace(/\s*\[.*?\]/g, "");
          return `${cleanedSpeaker},${start},${end} => ${text}`;
        }

        const webvttSpeakerMatch = line.match(/^([^:]+?)\s*\[.*?\]\s*:(.*)$/);
        if (webvttSpeakerMatch) {
          const [, speaker, rest] = webvttSpeakerMatch;
          return `${speaker}:${rest}`;
        }

        return line;
      })
      .join("\n");

    const blob = new Blob([processedTranscript], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    
    link.href = url;

    link.download = transcriptName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  const {
    mediaHandlers,
    wasPlayingRef,
    resetRecoveryState,
  } = useMediaPlaybackRecovery({
    mediaRef: () => (isAudioAsset(selectedTabFile) ? audioRef.current : videoRef.current),
    currentUrl: videoUrl,
    onUrlUpdate: (freshUrl) => {
      videoUrlRef.current = freshUrl;
    },
    refetchMediaUrl: refetchVideoUrl,
    refetchSpritesheetUrl,
    hasSpritesheet: !!spritesheetAsset?.interactionAssetId,
    isActive: !!selectedVideoId,
    getFallbackResumeTime: () => (selectedVideoId ? resumeTimesRef.current[selectedVideoId] ?? 0 : 0),
    onSeekingCallback: (seekTime) => {
      if (selectedVideoId) {
        resumeTimesRef.current[selectedVideoId] = seekTime;
      }
    },
    onTimeUpdateCallback: (currTime) => {
      if (selectedVideoId) {
        setResumeTimes((prev) => ({
          ...prev,
          [selectedVideoId]: currTime,
        }));
      }
    },
  });

  useEffect(() => {
    return () => {
      resetRecoveryState();
    };
  }, [selectedVideoId]);

  return (
  <>
    {savedfiles?.length === 0  ? (
      <Box className="callStats-noDataBox">
        <Typography variant="body2" className="callStats-noDataText">
          No data available
        </Typography>
      </Box>
    ) : transcriptLoading ? (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    ) : (
      (() => {
      const showTabs = tabFiles?.length > 1;
  
      if (!hasVideoOrAudio) {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
            }}
          >
            <Tabs
              value={0}
              aria-label="Transcript Tab"
              className="call-stats-recording-tabs"
            >
              <Tab
                label="Transcript"
                className="call-stats-recording-tab"
              />
            </Tabs>
            <Divider sx={{ width: "100%" }} />
          <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            height: "calc(100vh - 180px)",
            gap: 2,
            padding: 2,
          }}
        >
          <Box
            sx={{
              flex: "1 1 60%",
              overflowY: "auto",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              scrollbarWidth: "thin",
              p: 1,
              pt: 0,
            }}
            ref={containerRef}
          >
            {renderTranscriptContainer()}
          </Box>
          <Box sx={{ flex: "1 1 40%"}}>
            <ParticipantBox
              speakerSegmentsMap={speakerSegmentsMap}
              withoutDurationAll={withoutDurationAll}
              domain={domain}
              renderSpeaker={renderSpeaker}
              handleSegmentClick={handleSegmentClick}
              meetingDuration={meetingDuration}
              theme={theme}
            />
          </Box>
        </Box>
        </Box>
        );
      }
      return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
        {showTabs && (
        <>
        <Tabs
            className="call-stats-recording-tabs"
            value={selectedRecordingIndex}
            onChange={handleChangeTab}
            aria-label="Recording Tabs"
            variant="scrollable"
            scrollButtons={false}
            visibleScrollbar
          >
            {(() => {
              let videoIndex = 0;
              let audioIndex = 0;
              let transcriptIndex = 0;
              const totalVideos = tabFiles.filter((item) => isVideoAsset(item) && !isVideoClipAsset(item)).length;
              const totalAudios = tabFiles.filter(isAudioAsset).length;

              return tabFiles.map((item, idx) => {
                const name = item.assetName?.toLowerCase() || "";
                let label = "";
                const matchingAssets = isVideoClipAsset(item)
                  ? collateralsList.filter((asset: any) =>
                      item?.assetMetaData?.clip_id != null &&
                      String(asset?.assetMetaData?.clip_id) === String(item.assetMetaData.clip_id)
                    )
                  : collateralsList.filter((asset: any) =>
                      item?.interactionGroupId && asset?.interactionGroupId === item.interactionGroupId
                    );
                const clipRangeSources = [
                  item,
                  item?.assetMetaData,
                  ...matchingAssets,
                  ...matchingAssets.map((asset: any) => asset?.assetMetaData),
                ];
                const clipRange = clipRangeSources
                  .map((source: any) => ({
                    start: Number(source?.startTime ?? source?.start_time),
                    end: Number(source?.endTime ?? source?.end_time),
                  }))
                  .find(({ start, end }) => Number.isFinite(start) && Number.isFinite(end) && end > start);
                const clipDuration = clipRange ? clipRange.end - clipRange.start : undefined;
                const durationValue = isVideoClipAsset(item)
                  ? clipDuration
                  : [
                      item?.assetMetaData?.transcriptDuration,
                      ...matchingAssets.map((asset: any) => asset?.assetMetaData?.transcriptDuration),
                    ].find((value) => typeof value === "number" && value > 0);
                const durationLabel = typeof durationValue === "number"
                  ? secondsToHHMMSS(durationValue)
                  : "";

                if (isVideoClipAsset(item)) {
                  const clipFileName = (item.assetName || item.name || `Clip ${idx + 1}`)
                    .split("/")
                    .pop() || `Clip ${idx + 1}`;
                  label = clipFileName.replace(/_video_clip\.mp4$/i, "");
                } else if (isVideoAsset(item)) {
                  videoIndex++;
                  label = totalVideos > 1 ? `Full Recording ${videoIndex}` : "Full Recording";
                } else if (isAudioAsset(item)) {
                  audioIndex++;
                  label = totalAudios > 1 ? `Audio ${audioIndex}` : "Audio";
                } else if (name.endsWith(".txt")) {
                  transcriptIndex++;
                  label = `Transcript ${transcriptIndex}`;
                } else {
                  label = `Recording ${idx + 1}`;
                }

                return (
                  <Tab
                    key={item.interactionAssetId}
                    className="call-stats-recording-tab"
                    label={(
                      <Box className="call-stats-recording-tab-label">
                        <span className="call-stats-recording-tab-name">{label}</span>
                        {durationLabel && (
                          <span className="call-stats-recording-tab-duration">[{durationLabel}]</span>
                        )}
                      </Box>
                    )}
                  />
                );
              });
            })()}
          </Tabs>
          <Divider sx={{ width: "100%" }} />
          </>
        )}
        {(isVideoAsset(selectedTabFile) || isVideoClipAsset(selectedTabFile)) && (
          <Box
            className="callStats-transcriptBox"
            id="resizable-container"
            sx={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box className="callStats-layout"
              sx={{
                width: `${leftWidth}%`,
              }}
            >
              {(!isParticipantsExpanded || isSelectedClip) && (
              <Box className="callStats-video-layout"
                sx={{
                  flex: "0 1 auto",
                  minHeight: 0,
                }}
              >
                <Box className="callStats-videoWrapper" sx={{height: "auto"}} onClick={() => togglePlayPause(true)}>
                  <video
                    ref={videoRef as RefObject<HTMLVideoElement>}
                    src={videoUrl}
                    controlsList="nodownload"
                    disablePictureInPicture
                    width="100%"
                    style={{
                      borderRadius: "8px",
                      backgroundColor: "#000",
                      objectFit: "contain",
                      width: "100%",
                      height: "100%",
                    }}
                    onClick={() => togglePlayPause(false)}
                    {...mediaHandlers}
                  />
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                  <CustomVideoControls videoRef={videoRef} hideDownloadAndPiP
                    onClippingChange={setIsClipping}
                    clipEditorResetKey={clipEditorResetKey}
                    videoAsset={selectedVideoAsset}
                    originalVideoAssetId={selectedVideoAsset?.interactionAssetId || selectedVideoId}
                    originalCondensedTranscriptAssetId={originalCondensedTranscriptAssetId}
                    originalRawTranscriptAssetId={originalRawTranscriptAssetId}
                    groupId={selectedRecordingGroupId}
                    interactionId={interactionId}
                    spritesheetUrl={spritesheetDownloadData?.data?.downloadUrl}
                    clipRangeRequest={clipRangeRequest}
                    onClipSaved={onClipSaved}
                    referenceTime={effectiveReferenceTime?.toISOString() ?? null}
                    isClipped={isSelectedClip}
                    />
                </Box>
              </Box>
              )}
              {!isSelectedClip && !isParticipantsExpanded && !isClipping && <Divider/> }
              {!isSelectedClip && (
                <Box className="callStats-otherInviteesBox"
                  ref={containerRef}
                  sx={{
                    flex: "1 1 30%",
                    overflowY: "auto",
                    borderRadius: "0px",
                    p: 2,
                    paddingRight: "0px",
                    paddingBottom: "0px",
                    backgroundColor: "#fff",
                  }}
                >
                {!isClipping && (
                  <ParticipantBox
                    speakerSegmentsMap={speakerSegmentsMap}
                    withoutDurationAll={withoutDurationAll}
                    domain={domain}
                    renderSpeaker={renderSpeaker}
                    handleSegmentClick={handleSegmentClick}
                    meetingDuration={meetingDuration}
                    theme={theme}
                    isExpanded={isParticipantsExpanded}
                    onToggleExpand={() => setIsParticipantsExpanded((prev) => !prev)}
                  />
                )}
              </Box>
              )}
            </Box>
            <Box
              className="callStats-container-resize"
              onMouseDown={handleMouseDown}
            >
              <Box className="callStats-divider-line"/>
              <Box
                component="img"
                src={ColumnResizeIcon}
                className="callStats-column-resize-img"
                sx={{
                  width: 20,
                  height: 40,
                }}
              />
            </Box>
            <Box
            className="callStats-otherInviteesBox"
            ref={containerRef}
            sx={{
              overflowY: "auto",
              overflowX: "hidden",
              padding: "0px 6px 6px 6px",
              backgroundColor: "#fff",
              width: `${100 - leftWidth}%`,
            }}
          >
            {renderTranscriptContainer()}
          </Box>
          </Box>
        )}
        {isAudioAsset(selectedTabFile) && (
          <Box className="audio-file-container">
            <Box className="audio-file-container-box">
              <Typography className="aduio-file-name">
                {(selectedTabFile?.assetName)?.replace(/_/g, " ")}
              </Typography>
              <audio
                ref={audioRef}
                src={videoUrl}
                style={{ display: "none" }}
                {...mediaHandlers}
              />
              <Box sx={{ flexShrink: 0 }}>
                <CustomVideoControls
                  videoRef={audioRef}
                  hideDownloadAndPiP
                  onClippingChange={setIsClipping}
                  clipEditorResetKey={clipEditorResetKey}
                  videoAsset={selectedVideoAsset}
                  originalVideoAssetId={selectedVideoAsset?.interactionAssetId || selectedVideoId}
                  originalCondensedTranscriptAssetId={originalCondensedTranscriptAssetId}
                  originalRawTranscriptAssetId={originalRawTranscriptAssetId}
                  groupId={selectedRecordingGroupId}
                  interactionId={interactionId}
                  isAudio
                  clipRangeRequest={clipRangeRequest}
                  onClipSaved={onClipSaved}
                  isClipped={false}
                />
              </Box>
            </Box>
            <Divider/>
            <Box
              className="callStats-otherInviteesBox"
              ref={containerRef}
              sx={{margin: "8px"}}
            >
              {renderTranscriptContainer()}
            </Box>
          </Box>
        )}
      </Box>
      );
    })()
  )}
  </>
);
};

export default CallStatsPage; 
